"use client";

import { useEffect } from "react";
import posthog from "posthog-js";
import { readAnalyticsConsent } from "@/components/analytics/browser-attribution";
import { subscribeToBrowserConsentCategory } from "@/components/analytics/browser-consent-lifecycle";
import { POSTHOG_BROWSER_INITIALIZED_EVENT } from "@/lib/analytics/posthog-browser-lifecycle";
import {
  isGoogleTagDestinationReady,
  subscribeToGoogleTagDestinationReady,
} from "@/lib/analytics/google-tag-readiness";
import { capturePublishingEvent } from "@/lib/marketing/publishing-analytics";

type MediumBuilderCta =
  | "trial_start"
  | "pricing"
  | "handshq_comparison"
  | "business_plan"
  | "site_control"
  | "contact_sales";

const CTA_VALUES: ReadonlySet<MediumBuilderCta> = new Set([
  "trial_start",
  "pricing",
  "handshq_comparison",
  "business_plan",
  "site_control",
  "contact_sales",
]);

/** Consent-gated interaction and section tracking for the medium-builder route. */
export function MediumBuilderTracker() {
  useEffect(() => {
    let deactivateTracking: (() => void) | null = null;

    function activateTracking() {
      if (
        deactivateTracking ||
        readAnalyticsConsent() !== "accepted" ||
        !isGoogleTagDestinationReady("analytics") ||
        !posthog.__loaded
      ) {
        return;
      }

      function onClick(event: MouseEvent) {
        if (readAnalyticsConsent() !== "accepted") return;
        const target = event.target;
        if (!(target instanceof Element)) return;

        const anchor = target.closest<HTMLAnchorElement>(
          "a[data-medium-event]",
        );
        if (!anchor) return;

        const value = anchor.getAttribute("data-medium-event");
        if (!value || !CTA_VALUES.has(value as MediumBuilderCta)) return;

        capturePublishingEvent("publishing_cta_clicked", {
          surface: "medium_builder",
          cta_type: value,
          cta_location:
            anchor.getAttribute("data-medium-position") ?? "unspecified",
          selected_plan: anchor.getAttribute("data-selected-plan"),
          target_path: anchor.getAttribute("href"),
        });
      }

      const faqElements = Array.from(
        document.querySelectorAll<HTMLDetailsElement>(
          "[data-medium-faq-scope] details",
        ),
      );
      const faqListeners = faqElements.map((details, index) => {
        let wasOpen = details.open;
        const onToggle = () => {
          const openedByUser = details.open && !wasOpen;
          wasOpen = details.open;
          if (openedByUser && readAnalyticsConsent() === "accepted") {
            capturePublishingEvent("publishing_faq_expanded", {
              surface: "medium_builder",
              faq_index: index,
              faq_question:
                details.querySelector("summary")?.textContent?.trim() ?? null,
            });
          }
        };
        details.addEventListener("toggle", onToggle);
        return { details, onToggle };
      });

      const firedSections = new Set<string>();
      const observer = new IntersectionObserver(
        (entries) => {
          if (readAnalyticsConsent() !== "accepted") return;
          for (const entry of entries) {
            if (!entry.isIntersecting || entry.intersectionRatio < 0.5)
              continue;
            const sectionId = entry.target.getAttribute("data-medium-section");
            if (!sectionId || firedSections.has(sectionId)) continue;

            firedSections.add(sectionId);
            capturePublishingEvent("publishing_section_viewed", {
              surface: "medium_builder",
              section_id: sectionId,
            });
            observer.unobserve(entry.target);
          }
        },
        { threshold: 0.5 },
      );

      document
        .querySelectorAll<HTMLElement>("[data-medium-section]")
        .forEach((section) => observer.observe(section));
      document.addEventListener("click", onClick);

      deactivateTracking = () => {
        document.removeEventListener("click", onClick);
        observer.disconnect();
        faqListeners.forEach(({ details, onToggle }) => {
          details.removeEventListener("toggle", onToggle);
        });
      };
    }

    function reconcileTracking() {
      if (readAnalyticsConsent() === "accepted") {
        activateTracking();
      } else {
        deactivateTracking?.();
        deactivateTracking = null;
      }
    }

    reconcileTracking();
    window.addEventListener(
      POSTHOG_BROWSER_INITIALIZED_EVENT,
      reconcileTracking,
    );
    window.addEventListener("load", reconcileTracking);
    const unsubscribeConsent = subscribeToBrowserConsentCategory(
      "analytics",
      reconcileTracking,
    );
    const unsubscribeGoogleReady = subscribeToGoogleTagDestinationReady(
      "analytics",
      reconcileTracking,
    );

    return () => {
      deactivateTracking?.();
      window.removeEventListener(
        POSTHOG_BROWSER_INITIALIZED_EVENT,
        reconcileTracking,
      );
      window.removeEventListener("load", reconcileTracking);
      unsubscribeConsent();
      unsubscribeGoogleReady();
    };
  }, []);

  return null;
}
